import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { getField, RouteBuilders } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { useFormV2Context } from "@/features/FormV2/context/FormV2Context";
import { ThemedGeneralFundraiser } from "@/features/FormV2/sharedComponents/ThemedGeneralFundraiser";
import { ThemedTeamFundraiser } from "@/features/FormV2/sharedComponents/ThemedTeamFundraiser";
import { FormBlock } from "@/forms-design-system/FormBlock";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { trpc } from "@/helpers/trpc";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { useGetCampaignWithVolumeQuery } from "@/src/gql/queries/generated/campaignQuery";

interface ThemedHostedByProps {
  isIndividualCampaignInTeamCampaign?: boolean;
  campaignId: string;
  teamId?: string;
}

export const ThemedHostedBy: FC<ThemedHostedByProps> = ({
  isIndividualCampaignInTeamCampaign = false,
  campaignId,
  teamId,
}) => {
  const { locale, isoLocale } = useLocaleContext();

  const { formCurrency } = useFormV2Context();

  // Get campaign data information
  const { data: campaignData } = useGetCampaignWithVolumeQuery({
    variables: { campaignId: campaignId as string },
  });

  const campaignObject = campaignData?.getCampaignWithVolume?.object;
  const campaignLogoUrl = campaignData?.getCampaignWithVolume?.object?.logoUrl || undefined;
  const campaignTitle = getField(campaignData?.getCampaignWithVolume?.object?.donationFormFields, locale)?.title || "";
  const campaignOrganizationName = campaignData?.getCampaignWithVolume?.object?.organization?.name || "";

  // Get team data information
  const { data: teamData } = trpc.getTeamWithVolume.useQuery(
    { teamId: teamId as string },
    { enabled: Boolean(teamId) }
  );

  const teamLogoUrl = teamData?.logoUrl || undefined;
  const teamTitle = getField(teamData?.donationFormFields, locale)?.title || "";
  const teamVolume = teamData?.formVolume || 0;

  if (!campaignObject) {
    return null;
  }

  if (teamId && !teamData) {
    return null;
  }

  return (
    <StatelessThemedHostedBy
      slots={{
        generalFundraiser: (
          <ThemedGeneralFundraiser
            href={RouteBuilders.buildPeerToPeerV2GeneralCampaignLink({ isoLocale, path: campaignObject!.path }).path}
            imageSrc={campaignLogoUrl || null}
            organizationName={campaignOrganizationName}
            campaignTitle={campaignTitle}
            currency={formCurrency}
          />
        ),
        teamFundraiser: isIndividualCampaignInTeamCampaign ? (
          <ThemedTeamFundraiser
            href={RouteBuilders.buildPeerToPeerV2TeamCampaignLink({ isoLocale, path: teamData!.path }).path}
            imageSrc={teamLogoUrl || null}
            campaignTitle={teamTitle}
            totalRaised={teamVolume}
            currency={formCurrency}
          />
        ) : null,
      }}
    />
  );
};

type StatelessThemedHostedByProps = {
  slots: {
    generalFundraiser: React.ReactNode;
    teamFundraiser?: React.ReactNode;
  };
};

export const StatelessThemedHostedBy = ({ slots }: StatelessThemedHostedByProps) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const showHostedByTeam = !!slots.teamFundraiser;

  return (
    <FormBlock variant="card" fullWidth>
      <Stack direction="column" spacing={1}>
        <FormTypography variant="subtitle1" vibe="supershy">
          {t("donationForm", "hostedBy.title")}
        </FormTypography>
        {showHostedByTeam ? (
          <Stack direction={{ md: "row" }} gap={2} display="flex" justifyContent="stretch">
            <Box flex={1} sx={{ minWidth: 0, maxWidth: isSmallScreen ? "100%" : "50%" }}>
              <FormTypography variant="subtitle1" vibe="moderate" marginBottom={1}>
                {t("donationForm", "hostedBy.mainCampaign")}
              </FormTypography>
              {slots.generalFundraiser}
            </Box>
            <Box flex={1} sx={{ minWidth: 0, maxWidth: isSmallScreen ? "100%" : "50%" }}>
              <FormTypography variant="subtitle1" vibe="moderate" marginBottom={1}>
                {t("donationForm", "hostedBy.team")}
              </FormTypography>
              {slots.teamFundraiser}
            </Box>
          </Stack>
        ) : (
          slots.generalFundraiser
        )}
      </Stack>
    </FormBlock>
  );
};
